import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, Sparkles, ShieldCheck, ArrowRight, ChevronDown, Globe, Star } from 'lucide-react';
import { PLANS, FREE_PLAN, REGIONS, DEFAULT_REGION, BILLING_CYCLES, getPlanPrice, PILLARS, FAQS } from '@/lib/pricing';
import { formatMoney } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import { effectivePlan } from '@/lib/entitlements';
import { getBillingMe, startTrial, detectGeo } from '@/lib/billing';
import PlanSymbol from '@/components/PlanSymbol';

import usePageTitle from '@/hooks/usePageTitle';
export default function Pricing() {
  usePageTitle('Pricing');
  const { user, profile, session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [region, setRegion] = useState(DEFAULT_REGION);
  const [cycle, setCycle] = useState('monthly');
  const [billingMe, setBillingMe] = useState(null);
  const [trialBusy, setTrialBusy] = useState(null); // plan id currently submitting
  const r = REGIONS[region];
  // What plan does the signed-in user have right now? Used to surface the
  // "Your current plan" CTA so the upgrade flow self-explains.
  const currentPlan = user ? effectivePlan(profile) : null;
  // Phase 32, trial eligibility comes from /api/billing/me (lifetime-once flag).
  const trialEligible = !!billingMe?.trial_eligible;

  // Phase 32, auto-pick INR vs USD based on geolocation + cached profile.
  // Public visitors (signed-out) hit /api/geo/me; signed-in users get their
  // cached country from the profile. Toggle remains visible in PREVIEW only.
  // (`process.env.NODE_ENV === 'production'` proxies "we're on app.2120.money".)
  const isProduction = process.env.NODE_ENV === 'production';

  useEffect(() => {
    let cancelled = false;

    // Helper, coerce any country signal (code OR full name) to a region key.
    // Accepts 'IN', 'in', 'India', 'INDIA' (and the ISO-name variants Onboarding
    // saves) and decides India vs international. Backend normalises elsewhere
    // but the comparison here was previously strict-equal to 'IN' which broke
    // every India-stored-as-'India' profile.
    const toRegion = (countryRaw, currencyRaw) => {
      // If we already have an explicit currency, trust it first, it's the
      // single source of truth on the user's profile.
      const cur = (currencyRaw || '').trim().toUpperCase();
      if (cur === 'INR') return 'india';
      if (cur === 'USD') return 'international';
      const c = (countryRaw || '').trim().toLowerCase();
      if (!c) return null;
      if (c === 'in' || c === 'india' || c === 'ind') return 'india';
      return 'international';
    };

    // 0) Fastest path, the AuthContext profile is already cached client-side.
    // No await, no flash of wrong currency. Profile.currency is the authoritative
    // signal we set during onboarding. If we already have it, pin the region
    // and do NOT re-detect via geo, that's what was causing the random
    // INR ⇄ USD flips when CF-IPCountry flapped between edges.
    const profileRegion = (profile?.currency || profile?.country)
      ? toRegion(profile.country, profile.currency)
      : null;
    if (profileRegion) setRegion(profileRegion);

    (async () => {
      // 1) If signed-in, ALWAYS fetch billing state (we need trial_eligible
      // for the CTA logic). Only let it overwrite region if we don't already
      // trust the profile's currency.
      if (session?.access_token) {
        const me = await getBillingMe(session.access_token);
        if (!cancelled && me) {
          setBillingMe(me);
          if (!profileRegion) {
            const r1 = toRegion(me.country, me.currency);
            if (r1) { setRegion(r1); return; }
          } else {
            return; // profile is authoritative; don't re-detect
          }
        }
      }
      // 2) Signed-out OR no cached country/currency, fall through to geo.
      if (profileRegion) return;
      const geo = await detectGeo(session?.access_token);
      if (!cancelled && geo?.country) {
        const r2 = toRegion(geo.country, geo.currency);
        if (r2) setRegion(r2);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token, profile?.country, profile?.currency]);

  const goCheckout = (planId) => {
    if (user) navigate('/settings/billing?upgrade=' + planId + '&cycle=' + cycle);
    else navigate('/signup');
  };
  const goSignup = () => {
    if (user) navigate('/dashboard');
    else navigate('/signup');
  };

  const handleStartTrial = async (planId) => {
    if (!user) {
      // Send them through signup with intent preserved.
      navigate(`/signup?trial=${planId}`);
      return;
    }
    if (trialBusy) return;
    setTrialBusy(planId);
    const res = await startTrial(session?.access_token, planId);
    setTrialBusy(null);
    if (res?.ok) {
      toast.success(`Your ${planId === 'horizon' ? 'Horizon' : 'Summit'} trial begins now`, {
        description: `${res.days_left} days of full access. No card required.`,
      });
      await refreshProfile?.();
      navigate('/dashboard');
    } else {
      toast.error(res?.error || 'Could not start your trial right now.');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <div className="sticky top-0 z-30 backdrop-blur bg-background/70 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5" data-testid="pricing-logo-link">
            <img src="/brand/logo-mark.png" alt="2120" className="h-8 w-8" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-5 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-5 w-auto hidden dark:block" />
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard"><Button variant="ghost" className="rounded-xl">Back to app</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="rounded-xl" data-testid="pricing-login-link">Sign in</Button></Link>
                <Link to="/signup"><Button className="rounded-xl" data-testid="pricing-signup-link">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-hero-soft">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center fade-in">
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-gold" /> A calm, premium plan for every season of life
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-5">Choose the level of clarity that’s right for you.</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed max-w-2xl mx-auto">
            Whether you’re just getting organised, planning for the future, or managing money together, there’s a 2120 plan designed to help you understand your financial life more clearly.
          </p>

          {/* Billing cycle toggle */}
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <div className="inline-flex items-center rounded-full border border-border/70 bg-card p-1 shadow-brand" data-testid="pricing-cycle-toggle">
              {Object.values(BILLING_CYCLES).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCycle(c.id)}
                  data-testid={`cycle-${c.id}`}
                  className={`text-sm rounded-full px-4 py-1.5 transition-colors flex items-center gap-2 ${cycle === c.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {c.label}
                  {c.savePct && cycle !== c.id && (
                    <span className="text-[10px] font-medium text-gold">{c.savingsCopy || `Save ${c.savePct}%`}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Region toggle, visible in preview; auto-detected & hidden in production */}
            {!isProduction && (
            <div className="inline-flex items-center rounded-full border border-border/70 bg-card p-1 shadow-brand" data-testid="pricing-region-toggle">
              {Object.values(REGIONS).map((reg) => (
                <button
                  key={reg.id}
                  type="button"
                  onClick={() => setRegion(reg.id)}
                  data-testid={`region-${reg.id}`}
                  className={`text-sm rounded-full px-4 py-1.5 transition-colors flex items-center gap-2 ${region === reg.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <span className="text-base leading-none">{reg.flag}</span> {reg.label}
                </button>
              ))}
            </div>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-3 flex items-center justify-center gap-1.5"><Globe className="h-3 w-3" /> {r.note} · {cycle === 'annual' ? `Annual · ${BILLING_CYCLES.annual.savingsCopy}` : 'Monthly'}</div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-10 pb-16" data-testid="pricing-grid">
        <div className="grid lg:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const price = getPlanPrice(plan, region, cycle);
            const monthlyPrice = getPlanPrice(plan, region, 'monthly');
            const isHighlight = plan.highlight;
            const isAnnual = cycle === 'annual';
            const perLabel = isAnnual ? 'year' : 'month';
            const monthlyEquiv = isAnnual && price.monthlyEquiv ? price.monthlyEquiv : null;
            const cta = isAnnual ? plan.ctaAnnual : plan.ctaMonthly;
            // Phase 31, "Your current plan" CTA when the signed-in user is
            // already on this tier (or on its trial equivalent). The button
            // becomes a quiet, disabled affordance instead of an upsell so
            // the upgrade path is obvious: only OTHER cards are clickable.
            const isCurrent = currentPlan === plan.id;
            // Phase 31.A polish, annual savings (monthly\u00d712 − annual price)
            const annualSavings = isAnnual && monthlyPrice?.amount
              ? Math.max(0, Math.round((monthlyPrice.amount * 12 - price.amount) * 100) / 100)
              : 0;
            // Phase 31.A polish, Clarity uses an outline CTA so Horizon (gold)
            // is the unambiguous primary action. Hierarchy: outline < dark
            // green < gold.
            const isEntryTier = plan.id === 'clarity';
            // Phase 31.A polish, Trial line under price (Horizon & Summit).
            const trialDays = plan.trialDays || 0;
            return (
              <Card
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl border ${isCurrent
                  ? 'border-primary/40 ring-1 ring-primary/30 bg-card'
                  : isHighlight
                    ? 'border-[hsl(var(--gold))] shadow-brand-lg lg:-translate-y-3 bg-card'
                    : 'border-border/70 bg-card'} card-lift`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="rounded-full bg-primary text-primary-foreground hover:bg-primary border-0 px-3 py-1 text-[11px] tracking-wide shadow-brand">
                      <Star className="h-3 w-3 mr-1" /> Your current plan
                    </Badge>
                  </div>
                )}
                {isHighlight && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="rounded-full bg-gold text-[hsl(var(--gold-foreground))] hover:bg-gold border-0 px-3 py-1 text-[11px] tracking-wide shadow-brand">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 sm:p-7 space-y-5">
                  {/* Symbol */}
                  <div className="flex items-start justify-between">
                    <PlanSymbol symbol={plan.symbol} size={56} highlight={isHighlight} />
                    {isHighlight && <Sparkles className="h-4 w-4 text-gold mt-2" />}
                  </div>

                  {/* Header */}
                  <div>
                    <div className="font-display text-2xl font-semibold tracking-tight">{plan.name}</div>
                    <div className="text-sm text-primary mt-1 font-medium">{plan.tagline}</div>
                    <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{plan.positioning}</div>
                  </div>

                  {/* Price */}
                  <div className="py-3 border-y border-border/60">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums tracking-tight" data-testid={`plan-price-${plan.id}`}>
                        {formatMoney(price.amount, price.currency, { decimals: price.currency === 'USD' ? 2 : 0 })}
                      </div>
                      <div className="text-sm text-muted-foreground">/ {perLabel}</div>
                      {annualSavings > 0 && (
                        <span
                          data-testid={`plan-annual-savings-${plan.id}`}
                          className="ml-1 inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full bg-[hsl(var(--gold))]/12 text-[hsl(var(--gold))] border border-[hsl(var(--gold))]/30"
                          title="Compared to paying monthly for 12 months"
                        >
                          Save {formatMoney(annualSavings, price.currency, { decimals: price.currency === 'USD' ? 0 : 0 })}
                        </span>
                      )}
                    </div>
                    {monthlyEquiv ? (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        ≈ {formatMoney(monthlyEquiv, price.currency, { decimals: price.currency === 'USD' ? 2 : 0 })}/month · billed annually
                      </div>
                    ) : (
                      <div className="text-[11px] text-muted-foreground mt-1">Cancel anytime · switch plans whenever</div>
                    )}
                    {trialDays > 0 && (
                      <div
                        data-testid={`plan-trial-line-${plan.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-primary bg-accent/60 border border-border/60 rounded-full px-2.5 py-1"
                      >
                        <Sparkles className="h-3 w-3 text-gold" />
                        Try free for {trialDays} days · then {formatMoney(monthlyPrice.amount, monthlyPrice.currency, { decimals: monthlyPrice.currency === 'USD' ? 2 : 0 })}/month
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed">{plan.description}</p>

                  {/* Best for, single calm line from the PDF copy. */}
                  {plan.bestFor && (
                    <div className={`text-[11px] font-medium leading-relaxed rounded-xl px-3 py-2 ${isHighlight ? 'bg-[hsl(var(--gold))]/10 text-foreground/80 ring-1 ring-[hsl(var(--gold))]/30' : 'bg-accent/40 text-foreground/75 ring-1 ring-border/40'}`}>
                      <span className="text-muted-foreground/85">Best for </span>{plan.bestFor}
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-2.5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <div className={`mt-0.5 h-4 w-4 rounded-full grid place-items-center shrink-0 ${isHighlight ? 'bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]' : 'bg-accent text-primary'}`}>
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </div>
                        <span className="text-foreground/90 leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.upgradeHint && (
                    <div className="rounded-xl bg-accent/50 border border-border/60 p-3 text-[12px] text-muted-foreground leading-relaxed">
                      {plan.upgradeHint}
                    </div>
                  )}

                  {/* CTA, disabled and rebranded when this IS the user's
                      active plan, so the upgrade flow on other cards stands
                      out clearly. Phase 31.A: Clarity uses an outline variant
                      so Horizon (gold) is the unambiguous primary CTA.
                      Phase 32: Horizon/Summit show "Start 14-day free trial"
                      when the user is eligible (signed in + lifetime trial
                      unused). */}
                  {(() => {
                    const canTrial = !isCurrent && trialDays > 0 && (
                      // Signed-in user with unused trial
                      (user && trialEligible) ||
                      // Signed-out user, we'll route them through signup with intent
                      !user
                    );
                    const onClick = () => {
                      if (isCurrent) return;
                      if (canTrial) handleStartTrial(plan.id);
                      else goCheckout(plan.id);
                    };
                    const busy = trialBusy === plan.id;
                    const label = isCurrent
                      ? null
                      : canTrial
                        ? `Start ${trialDays}-day free trial`
                        : cta;
                    return (
                      <Button
                        onClick={onClick}
                        disabled={isCurrent || busy}
                        data-testid={`plan-cta-${plan.id}`}
                        variant={!isCurrent && isEntryTier ? 'outline' : 'default'}
                        className={`w-full rounded-xl ${isCurrent
                          ? 'bg-muted text-muted-foreground cursor-default hover:bg-muted'
                          : isHighlight
                            ? 'bg-gold text-[hsl(var(--gold-foreground))] hover:opacity-90'
                            : isEntryTier
                              ? 'border-primary/60 text-primary hover:bg-accent/60'
                              : ''}`}
                      >
                        {isCurrent ? (
                          <><Star className="h-4 w-4 mr-1.5 fill-current" /> Your current plan</>
                        ) : busy ? (
                          <>Starting your trial…</>
                        ) : (
                          <>{label} <ArrowRight className="h-4 w-4 ml-1" /></>
                        )}
                      </Button>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* -----------------------------------------------------------------
             Free tier, placed BELOW the three paid columns so it doesn't
             compete for attention with the main conversion story. Quiet
             dignity: a single horizontal card, dashed border, neutral
             accent. The "Your current plan" treatment applies here too so
             a signed-in Free user sees their state mirrored consistently.
             ---------------------------------------------------------------- */}
        <div className="mt-8" data-testid="pricing-free-row">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 border-t border-border/60"></div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Just exploring?</div>
            <div className="flex-1 border-t border-border/60"></div>
          </div>
          {(() => {
            const isCurrentFree = currentPlan === 'free';
            return (
              <Card
                data-testid="plan-card-free"
                className={`relative rounded-2xl border ${isCurrentFree
                  ? 'border-primary/40 ring-1 ring-primary/30 bg-card'
                  : 'border-dashed border-border/80 bg-card/60'} card-lift`}
              >
                {isCurrentFree && (
                  <div className="absolute -top-3 left-6">
                    <Badge className="rounded-full bg-primary text-primary-foreground hover:bg-primary border-0 px-3 py-1 text-[11px] tracking-wide shadow-brand">
                      <Star className="h-3 w-3 mr-1" /> Your current plan
                    </Badge>
                  </div>
                )}
                <CardContent className="p-5 sm:p-6 grid md:grid-cols-[1.4fr_1.6fr_auto] gap-5 items-center">
                  {/* Identity */}
                  <div className="flex items-center gap-4">
                    <PlanSymbol symbol={FREE_PLAN.symbol} size={48} highlight={false} />
                    <div>
                      <div className="font-display text-xl font-semibold tracking-tight">{FREE_PLAN.name}</div>
                      <div className="text-sm text-primary font-medium mt-0.5">{FREE_PLAN.tagline}</div>
                      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{FREE_PLAN.positioning}</div>
                    </div>
                  </div>
                  {/* Features, compact two-column inline list */}
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {FREE_PLAN.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[12.5px]">
                        <div className="mt-0.5 h-3.5 w-3.5 rounded-full grid place-items-center shrink-0 bg-accent text-primary">
                          <Check className="h-2 w-2" strokeWidth={3} />
                        </div>
                        <span className="text-foreground/80 leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>
                  {/* CTA */}
                  <div className="flex md:justify-end">
                    <Button
                      onClick={() => !isCurrentFree && goSignup()}
                      disabled={isCurrentFree}
                      data-testid="plan-cta-free"
                      variant={isCurrentFree ? 'default' : 'outline'}
                      className={`rounded-xl whitespace-nowrap ${isCurrentFree ? 'bg-muted text-muted-foreground cursor-default hover:bg-muted' : ''}`}
                    >
                      {isCurrentFree ? (
                        <><Star className="h-4 w-4 mr-1.5 fill-current" /> Your current plan</>
                      ) : (
                        <>{FREE_PLAN.ctaMonthly} <ArrowRight className="h-4 w-4 ml-1" /></>
                      )}
                    </Button>
                  </div>
                </CardContent>
                {/* Subtle upgrade nudge for Free users */}
                {FREE_PLAN.upgradeHint && (
                  <div className="border-t border-border/60 px-5 sm:px-6 py-3 text-[11.5px] text-muted-foreground italic">
                    {FREE_PLAN.upgradeHint}
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Trust strip */}
        <div className="mt-10 grid sm:grid-cols-3 gap-3">
          {PILLARS.map((p) => (
            <Card key={p.title} className="rounded-2xl border-border/70">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <div className="text-sm font-medium">{p.title}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{p.body}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Multi-currency callout (positioned as a feature, not the identity) */}
      <section className="bg-soft-gradient border-y border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground"><Globe className="h-3.5 w-3.5" /> Unified Financial Clarity Across Borders</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-4">One picture. Many currencies. No noise.</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              When your life spans countries, salary in one currency, investments in another, savings in a third, 2120 brings it all into a single, calm financial picture, in your chosen base currency. Available from Horizon upward.
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {['INR','USD','AED','EUR','GBP','SGD','CAD','AUD','SAR','QAR','KWD'].map((c) => (
                <Badge key={c} variant="secondary" className="rounded-full text-[11px] px-2.5 py-1">{c}</Badge>
              ))}
            </div>
          </div>
          <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 text-xs text-primary-foreground/70"><Sparkles className="h-3.5 w-3.5 text-gold" /> Sample picture</div>
              <div className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-2">Net worth</div>
              <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums tracking-tight mt-2">$84,210</div>
              <div className="mt-4 space-y-2 text-sm">
                <Row label="Salary · AED" value="AED 24,500" />
                <Row label="Investments · INR" value="₹1,86,000" />
                <Row label="Savings · USD" value="$48,210" />
                <Row label="Loans · INR" value="-₹18,40,000" />
              </div>
              <div className="mt-4 text-[11px] text-primary-foreground/60">All amounts shown in your base currency on the dashboard. Original currency is always preserved.</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-center">Honest questions, clear answers</h2>
        <div className="mt-6 space-y-2" data-testid="pricing-faq">
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} testid={`faq-${i}`} />)}
        </div>
      </section>

      {/* Foot */}
      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <img src="/brand/logo-mark.png" alt="" className="h-5 w-5" />
            <span>2120 · Calmer money clarity</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="/terms"   className="hover:text-foreground transition-colors" data-testid="pricing-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="pricing-footer-privacy">Privacy</Link>
            <Link to="/refunds" className="hover:text-foreground transition-colors" data-testid="pricing-footer-refunds">Refunds</Link>
            <span className="text-muted-foreground/60">·</span>
            <span>Pricing shown in {r.currency} · {cycle === 'annual' ? 'Annual billing' : 'Monthly billing'} · Geolocation auto-detection coming on launch.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-t border-white/10 pt-2 first:border-0 first:pt-0">
      <div className="text-primary-foreground/70">{label}</div>
      <div className="tabular-nums font-medium">{value}</div>
    </div>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-border/70 bg-card" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
        <span className="text-sm font-medium">{q}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">{a}</div>}
    </div>
  );
}
